"use client";
import { appUrls } from "@/constants";
import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import type { ILink } from "@ui/components/core/Link";
import { Button } from "@ui/components/core/Button";
import { LinkTracked } from "@ui/components/core/LinkTracked";
import { Howl } from "howler";
import { PauseIcon, PlayIcon, SkipBackIcon, SkipForwardIcon } from "lucide-react";
import { AnimatePresence } from "motion/react";
import * as m from "motion/react-m";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";

import type { AlbumCover } from "./AlbumCoverPlayer";
import { ContextualNavigationTracker } from "@ui/components/navigation/ContextualNavigation";
import dynamic from "next/dynamic";

const AlbumCoverVisual = dynamic(() => import("./AlbumCoverPlayer").then(mod => ({ default: mod.AlbumCoverVisual })));

const DEFAULT_COVERS: AlbumCover[] = [
  {
    imageUrl: "/assets/images/music/02.webp",
    audioUrl: "/assets/audio/music/02_Echoes_of_Midnight.mp3",
    id: "1",
  },
  {
    imageUrl: "/assets/images/music/01.webp",
    audioUrl: "/assets/audio/music/01_Adrenalina.mp3",
    id: "2",
  },
  {
    imageUrl: "/assets/images/music/03.webp",
    audioUrl: "/assets/audio/music/03_Hip_Hop.mp3",
    id: "3",
  },
  {
    imageUrl: "/assets/images/music/04.webp",
    audioUrl: "/assets/audio/music/04_Indie_Rock.mp3",
    id: "4",
  },
  {
    imageUrl: "/assets/images/music/05.webp",
    audioUrl: "/assets/audio/music/05_Saddles_and_Shadows.mp3",
    id: "5",
  },
  {
    imageUrl: "/assets/images/music/06.webp",
    audioUrl: "/assets/audio/music/06_Afrobeat.mp3",
    id: "6",
  },
  {
    imageUrl: "/assets/images/music/07.webp",
    audioUrl: "/assets/audio/music/07_Endless_Dream.mp3",
    id: "7",
  },
  {
    imageUrl: "/assets/images/music/08.webp",
    audioUrl: "/assets/audio/music/08_Flamenco.mp3",
    id: "8",
  },
  {
    imageUrl: "/assets/images/music/09.webp",
    audioUrl: "/assets/audio/music/09_Dub_Reggae.mp3",
    id: "9",
  },
  {
    imageUrl: "/assets/images/music/10.webp",
    audioUrl: "/assets/audio/music/10_Don_t_Let_Me_Go.mp3",
    id: "10",
  },
  {
    imageUrl: "/assets/images/music/11.webp",
    audioUrl: "/assets/audio/music/11_Undecim_Anthem.mp3",
    id: "11",
  },
  {
    imageUrl: "/assets/images/music/12.webp",
    audioUrl: "/assets/audio/music/12_Slip_Away.mp3",
    id: "12",
  },
  {
    imageUrl: "/assets/images/music/13.webp",
    audioUrl: "/assets/audio/music/13_Modal_Jazz.mp3",
    id: "13",
  },
];

export function HeroMusic({
  title,
  description,
  ctaLink,
  secondaryCtaLink,
}: {
  title?: string | null;
  description?: string | null;
  ctaLink?: ILink | null;
  secondaryCtaLink?: ILink | null;
}) {
  const t = useTranslations("ClientKeys.music.hero");
  const howlRef = useRef<Howl>(null);
  const [activeCoverIndex, setActiveCoverIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  function startNewSong(index: number) {
    if (howlRef.current) {
      howlRef.current.stop();
      howlRef.current.unload();
    }
    howlRef.current = new Howl({
      src: [DEFAULT_COVERS[index].audioUrl!],
      preload: false,
      autoplay: false,
      html5: true,
    });
    if (isPlaying) {
      howlRef.current?.play();
    }
  }

  // Start new song when activeCoverIndex changes (from navigation or dragging)
  useEffect(() => {
    startNewSong(activeCoverIndex);
  }, [activeCoverIndex]);

  function navigate(direction: "next" | "prev") {
    const newIndex =
      (activeCoverIndex + (direction === "next" ? 1 : -1) + DEFAULT_COVERS.length) % DEFAULT_COVERS.length;
    setActiveCoverIndex(newIndex);
  }

  function togglePlay() {
    const newIsPlaying = !isPlaying;
    if (newIsPlaying) {
      if (!howlRef.current) {
        startNewSong(activeCoverIndex);
      }
      howlRef.current?.play();
      captureEvent(EventName.mw_music_hero_sample_played, {
        sample_index: activeCoverIndex,
        sample_id: DEFAULT_COVERS[activeCoverIndex].id,
      });
    } else {
      howlRef.current?.pause();
      captureEvent(EventName.mw_music_hero_sample_paused, {
        sample_index: activeCoverIndex,
        sample_id: DEFAULT_COVERS[activeCoverIndex].id,
      });
    }

    setIsPlaying(newIsPlaying);
  }

  return (
    <div className='container flex min-h-fit flex-col items-center justify-center'>
      <AlbumCoverVisual
        activeCoverIndex={activeCoverIndex}
        covers={DEFAULT_COVERS}
        onActiveCoverIndexChange={setActiveCoverIndex}
      />
      <div className='relative z-4 -mt-40 flex flex-col items-center justify-center gap-12'>
        <div className='flex items-center gap-4'>
          <NavButton onClick={() => navigate("prev")} type='prev' />
          <Button onClick={togglePlay} size='pill' variant='primary'>
            {isPlaying ? <PauseIcon fill='currentColor' size={14} /> : <PlayIcon fill='currentColor' size={14} />}
          </Button>
          <NavButton onClick={() => navigate("next")} type='next' />
        </div>
        <div className='flex items-center justify-center'>
          <AnimatePresence initial={false} mode='wait'>
            <m.div
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              className='use-gpu f-paragraph-03 w-[300px] text-center text-light will-change-transform'
              exit={{
                opacity: 0,
                y: -2,
                filter: "blur(4px)",
                transition: { duration: 0.5 },
              }}
              initial={{ opacity: 0, y: 2, filter: "blur(4px)" }}
              key={activeCoverIndex}
              transition={{ duration: 0.4 }}
            >
              {t(`track_descriptions.${activeCoverIndex}` as any)}
            </m.div>
          </AnimatePresence>
        </div>
      </div>
      <div className='flex flex-col justify-start pt-64 text-center'>
        <div className='tw-type-6xl'>
          <AnimatePresence mode='wait'>
            <m.h1
              animate={{ backgroundPositionY: 0 }}
              className='tw-text-transparent tw-text-balance tw-max-w-2xl tw-text-center tw-mx-auto tw-bg-clip-text tw-bg-center'
              exit={{ backgroundPositionY: "100%" }}
              initial={{ backgroundPositionY: "100%" }}
              key={activeCoverIndex}
              style={{
                backgroundImage: `url(/assets/images/music/blur-sprite.jpg)`,
                backgroundSize: "1300% 200%",
                backgroundPositionX: `${activeCoverIndex * (100 / 12)}%`,
                backgroundPositionY: "top",
              }}
              transition={{ duration: 0.5 }}
            >
              {title || t("hero_title")}
            </m.h1>
          </AnimatePresence>
        </div>
        <h2 className='tw-mt-8 tw-type-base tw-text-black tw-text-balance tw-max-w-2xl tw-mx-auto tw-text-center'>
          {description || t("hero_description")}
        </h2>
        <div className='tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-2 tw-pt-8 tw-pb-6'>
          {ctaLink ? (
            <LinkTracked
              {...ctaLink}
              asButton
              buttonVariants={{ variant: "primary", size: "lg" }}
              eventName={EventName.mw_hero_primary_link}
            />
          ) : (
            <LinkTracked
              asButton
              buttonVariants={{ variant: "primary", size: "lg" }}
              eventName={EventName.mw_hero_primary_link}
              href={appUrls.music}
            >
              {t("get_started")}
            </LinkTracked>
          )}
          {secondaryCtaLink && (
            <LinkTracked
              {...secondaryCtaLink}
              asButton
              buttonVariants={{ variant: "elevated", size: "lg" }}
              eventName={EventName.mw_hero_secondary_link}
            />
          )}
        </div>
        <ContextualNavigationTracker />
      </div>
    </div>
  );
}

const NavButton = ({ onClick, type }: { onClick: () => void; type: "next" | "prev" }) => {
  return (
    <Button className='text-light' onClick={onClick} size='pill' variant='ghost'>
      {type === "prev" ? (
        <SkipBackIcon fill='currentColor' size={16} />
      ) : (
        <SkipForwardIcon fill='currentColor' size={16} />
      )}
    </Button>
  );
};
